import React from 'react';
import HelpBadge from './components/HelpBadge';
import './UxOpsAdmin.css';

type UxOverview = {
  window_hours?: number;
  by_type?: Record<string, number>;
  by_product?: Record<string, number>;
  by_device?: Record<string, number>;
  funnels?: Record<string, Record<string, number>>;
  open_cases?: number;
  outreach_queue?: number;
  mobile_short_sessions?: number;
  login_failures?: number;
  upload_failures?: number;
  client_errors?: number;
  error_correlated_abandons?: number;
};

type UxCase = {
  case_id: number;
  event_type: string;
  product: string;
  funnel_key?: string | null;
  step?: string | null;
  last_path?: string | null;
  session_id?: string | null;
  email?: string | null;
  device_class?: string | null;
  severity: string;
  status: string;
  title: string;
  notes?: string | null;
  owner?: string | null;
  outreach_at?: string | null;
  created_at: string;
  outreach?: { subject?: string; body?: string; email?: string };
};

type ExperienceAdminSectionProps = {
  gatewayBaseUrl: string;
  accessToken: string | null;
};

const STATUSES = ['new', 'investigating', 'outreach', 'waiting', 'fixed', 'wont_fix'] as const;

const ExperienceAdminSection: React.FC<ExperienceAdminSectionProps> = ({
  gatewayBaseUrl,
  accessToken,
}) => {
  const [overview, setOverview] = React.useState<UxOverview | null>(null);
  const [cases, setCases] = React.useState<UxCase[]>([]);
  const [productFilter, setProductFilter] = React.useState('all');
  const [openOnly, setOpenOnly] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<number | null>(null);
  const [timeline, setTimeline] = React.useState<{ caseId: number; events: Array<{ event_type: string; step?: string; created_at: string; message?: string }> } | null>(null);

  const callApi = React.useCallback(async (path: string, init: RequestInit = {}) => {
    const response = await fetch(`${gatewayBaseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(init.headers || {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Request failed (HTTP ${response.status})`);
    }
    return response.json();
  }, [accessToken, gatewayBaseUrl]);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const query = new URLSearchParams();
      if (productFilter !== 'all') query.set('product', productFilter);
      if (openOnly) query.set('open_only', 'true');
      const suffix = query.toString() ? `?${query.toString()}` : '';
      const [overviewPayload, casesPayload] = await Promise.all([
        callApi('/api/v1/ux/overview'),
        callApi(`/api/v1/ux/cases${suffix}`),
      ]);
      setOverview(overviewPayload as UxOverview);
      setCases(Array.isArray((casesPayload as { cases?: UxCase[] }).cases) ? (casesPayload as { cases: UxCase[] }).cases : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load experience cases.');
    }
  }, [callApi, openOnly, productFilter]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const updateCase = async (caseId: number, patch: { status?: string; notes?: string }) => {
    setBusyId(caseId);
    try {
      await callApi(`/api/v1/ux/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update case.');
    } finally {
      setBusyId(null);
    }
  };

  const reachOut = async (entry: UxCase) => {
    const draft = entry.outreach || {};
    if (draft.email) {
      const href = `mailto:${encodeURIComponent(draft.email)}?subject=${encodeURIComponent(draft.subject || entry.title)}&body=${encodeURIComponent(draft.body || '')}`;
      window.location.assign(href);
    }
    await updateCase(entry.case_id, {
      status: 'outreach',
      notes: entry.notes || 'Opened outreach email from the triage board.',
    });
  };

  const loadTimeline = async (entry: UxCase) => {
    if (!entry.session_id) {
      setTimeline({ caseId: entry.case_id, events: [] });
      return;
    }
    try {
      const payload = await callApi(`/api/v1/ux/events?session_id=${encodeURIComponent(entry.session_id)}&limit=50`);
      setTimeline({
        caseId: entry.case_id,
        events: Array.isArray((payload as { events?: unknown[] }).events)
          ? (payload as { events: Array<{ event_type: string; step?: string; created_at: string; message?: string }> }).events
          : [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load session timeline.');
    }
  };

  return (
    <div className="theme-admin-card ux-ops-card">
      <div className="admin-console-toolbar">
        <div>
          <h2>
            User experience
            <HelpBadge label="Help about experience triage">
              This board is the close-the-loop view: failed logins, failed uploads, abandoned signup/demo/research
              jobs, and short tablet/mobile sessions. Open a case, reach out, then mark it fixed.
            </HelpBadge>
          </h2>
          <p>Log, triage, and follow up on the places people get stuck across website, Pursuit, Vantage, and Inlet.</p>
        </div>
        <div className="admin-console-inline-actions">
          <label className="ux-ops-filter">
            <span>Product</span>
            <select value={productFilter} onChange={(event) => setProductFilter(event.target.value)}>
              <option value="all">All</option>
              <option value="website">Website</option>
              <option value="console">Console</option>
              <option value="pursuit">Pursuit</option>
              <option value="vantage">Vantage</option>
              <option value="inlet">Inlet</option>
            </select>
          </label>
          <label className="ux-ops-filter">
            <span>Cases</span>
            <select value={openOnly ? 'open' : 'all'} onChange={(event) => setOpenOnly(event.target.value === 'open')}>
              <option value="open">Open</option>
              <option value="all">All</option>
            </select>
          </label>
          <button type="button" className="btn btn-secondary" onClick={() => void load()}>Refresh</button>
        </div>
      </div>

      {error ? <p className="admin-console-error" role="alert">{error}</p> : null}

      <div className="ux-ops-grid">
        <article className="ux-ops-stat">
          <p>Open cases</p>
          <strong>{overview?.open_cases ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Outreach queue</p>
          <strong>{overview?.outreach_queue ?? 0}</strong>
          <span>Abandoned applications</span>
        </article>
        <article className="ux-ops-stat">
          <p>Login failures</p>
          <strong>{overview?.login_failures ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Upload failures</p>
          <strong>{overview?.upload_failures ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Short mobile/tablet</p>
          <strong>{overview?.mobile_short_sessions ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>JS / client errors</p>
          <strong>{overview?.client_errors ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Abandon + error</p>
          <strong>{overview?.error_correlated_abandons ?? 0}</strong>
          <span>Same session had a fault</span>
        </article>
      </div>

      <h3>By product</h3>
      <p className="ux-ops-chips">
        {Object.entries(overview?.by_product || {}).map(([product, count]) => (
          <span key={product}>{product}: {count}</span>
        ))}
        {Object.keys(overview?.by_product || {}).length === 0 ? 'No events in the last 24 hours.' : null}
      </p>

      <h3>Funnels (last step seen)</h3>
      {Object.keys(overview?.funnels || {}).length === 0 ? (
        <p className="theme-admin-message">No funnel steps recorded yet. Signup, demo request, and research jobs write here.</p>
      ) : (
        <div className="ux-ops-table-wrap">
          <table className="ux-ops-table">
            <thead>
              <tr>
                <th>Funnel</th>
                <th>Step</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(overview?.funnels || {}).flatMap(([funnel, steps]) => (
                Object.entries(steps).map(([step, count]) => (
                  <tr key={`${funnel}-${step}`}>
                    <td>{funnel}</td>
                    <td>{step}</td>
                    <td>{count}</td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Triage board</h3>
      {cases.length === 0 ? (
        <p className="theme-admin-message">No open experience cases. Failed logins, uploads, and abandoned flows appear here automatically.</p>
      ) : (
        <div className="ux-ops-table-wrap">
          <table className="ux-ops-table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Product</th>
                <th>Where they stopped</th>
                <th>Who</th>
                <th>Device</th>
                <th>Status</th>
                <th>Notes / outreach</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((entry) => (
                <tr key={entry.case_id}>
                  <td>
                    <strong>{entry.title}</strong>
                    <div>{entry.severity} · {entry.event_type}</div>
                  </td>
                  <td>{entry.product}</td>
                  <td>
                    {entry.step || '—'}
                    <div>{entry.last_path || entry.funnel_key || ''}</div>
                  </td>
                  <td>{entry.email || entry.user_id || 'anonymous'}</td>
                  <td>{entry.device_class || 'unknown'}</td>
                  <td>
                    <select
                      value={entry.status}
                      disabled={busyId === entry.case_id}
                      onChange={(event) => void updateCase(entry.case_id, { status: event.target.value })}
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        const form = event.currentTarget;
                        const notes = String(new FormData(form).get('notes') || '');
                        void updateCase(entry.case_id, { notes });
                      }}
                    >
                      <textarea name="notes" defaultValue={entry.notes || ''} rows={2} aria-label={`Notes for case ${entry.case_id}`} />
                      <button type="submit" className="btn btn-secondary" disabled={busyId === entry.case_id}>Save</button>
                    </form>
                    <div className="ux-ops-case-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={busyId === entry.case_id || !entry.email}
                        onClick={() => void reachOut(entry)}
                      >
                        Reach out
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => void loadTimeline(entry)}
                      >
                        Session
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {timeline ? (
        <div className="ux-ops-timeline">
          <h3>Session for case {timeline.caseId}</h3>
          {timeline.events.length === 0 ? (
            <p className="theme-admin-message">No events on this session yet.</p>
          ) : (
            <ol>
              {timeline.events.map((event, index) => (
                <li key={`${event.created_at}-${index}`}>
                  <strong>{event.event_type}</strong>
                  {event.step ? ` · ${event.step}` : ''}
                  {event.message ? ` — ${event.message}` : ''}
                  <span> {event.created_at}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ExperienceAdminSection;
